import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, Plane } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const RATE_PER_KG = 15;
const RATE_PER_LB = 6.8;
const KG_TO_LB = 2.20462;

const CostCalculator = () => {
  const { t, lang } = useLang();
  const [unit, setUnit] = useState("kg"); // "kg" | "lb"
  const [weight, setWeight] = useState(2);

  const isKg = unit === "kg";
  const rate = isKg ? RATE_PER_KG : RATE_PER_LB;
  const total = (weight * rate).toFixed(2);
  const unitLabel = isKg ? "kg" : "lb";

  // Convert weight when toggling units so the value reflects the same physical mass
  const switchUnit = (next) => {
    if (next === unit) return;
    if (next === "lb" && unit === "kg") {
      setWeight(parseFloat((weight * KG_TO_LB).toFixed(1)));
    } else if (next === "kg" && unit === "lb") {
      setWeight(parseFloat((weight / KG_TO_LB).toFixed(1)));
    }
    setUnit(next);
  };

  // Slider/input ranges adapt to selected unit
  const sliderMin = isKg ? 0.5 : 1;
  const sliderMax = isKg ? 50 : 110;
  const sliderStep = isKg ? 0.5 : 1;
  const sliderMid = isKg ? 25 : 55;

  return (
    <section id="calculator" data-testid="calculator-section" className="relative py-20 sm:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative rounded-[28px] bg-brand-cream border border-brand-navy/10 p-8 sm:p-12 overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-red/15 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-brand-blue/15 blur-3xl" aria-hidden="true" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-4">
                <Calculator size={14} className="text-brand-red" />
                <span className="text-xs font-bold uppercase tracking-[0.22em] text-brand-red">
                  {t.calc.eyebrow}
                </span>
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] text-brand-navy leading-[1.05]">
                {t.calc.title}
              </h2>
              <p className="mt-4 text-base text-brand-navy/70 leading-relaxed max-w-md">
                {t.calc.sub}
              </p>

              {/* Unit toggle */}
              <div
                role="tablist"
                aria-label={t.calc.unitToggleLabel}
                data-testid="calc-unit-toggle"
                className="mt-7 inline-flex items-center rounded-full bg-white border border-brand-navy/15 p-1 shadow-sm"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={isKg}
                  data-testid="calc-unit-kg"
                  onClick={() => switchUnit("kg")}
                  className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-heading font-bold transition-all ${
                    isKg
                      ? "bg-brand-navy text-brand-cream shadow"
                      : "text-brand-navy/65 hover:text-brand-navy"
                  }`}
                >
                  {t.calc.unitKg}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isKg}
                  data-testid="calc-unit-lb"
                  onClick={() => switchUnit("lb")}
                  className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-heading font-bold transition-all ${
                    !isKg
                      ? "bg-brand-navy text-brand-cream shadow"
                      : "text-brand-navy/65 hover:text-brand-navy"
                  }`}
                >
                  {t.calc.unitLb}
                </button>
              </div>

              <div className="mt-5">
                <label htmlFor="weight" className="block text-sm font-bold text-brand-navy mb-3">
                  {t.calc.label}:{" "}
                  <span className="text-brand-red font-black" data-testid="calc-weight-display">
                    {weight} {unitLabel}
                  </span>
                </label>
                <input
                  id="weight"
                  type="range"
                  min={sliderMin}
                  max={sliderMax}
                  step={sliderStep}
                  value={weight}
                  onChange={(e) => setWeight(parseFloat(e.target.value))}
                  data-testid="calc-weight-slider"
                  className="w-full h-2 bg-brand-navy/15 rounded-full appearance-none cursor-pointer accent-brand-red"
                />
                <div className="flex justify-between text-[11px] text-brand-navy/55 font-semibold mt-2">
                  <span>{sliderMin} {unitLabel}</span>
                  <span>{sliderMid} {unitLabel}</span>
                  <span>{sliderMax} {unitLabel}</span>
                </div>

                <div className="mt-5 flex items-center gap-3">
                  <input
                    type="number"
                    min="0.1"
                    max="1000"
                    step="0.1"
                    value={weight}
                    onChange={(e) => setWeight(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                    data-testid="calc-weight-input"
                    className="w-24 rounded-full bg-white border border-brand-navy/15 px-4 py-2 text-brand-navy font-semibold text-center focus:outline-none focus:ring-2 focus:ring-brand-red/40"
                  />
                  <span className="text-sm text-brand-navy/65 font-semibold">
                    {isKg ? t.calc.kgs : t.calc.lbs}
                  </span>
                </div>

                <div className="mt-3 text-xs text-brand-navy/55 font-medium" data-testid="calc-rate-display">
                  {t.calc.rate}: <span className="font-bold text-brand-navy">${rate.toFixed(2)} / {unitLabel}</span>
                </div>
              </div>
            </div>

            <div className="relative rounded-3xl bg-brand-navy text-brand-cream p-8 sm:p-10 overflow-hidden shadow-[0_25px_50px_-15px_rgba(14,42,86,0.4)]">
              <Plane size={120} strokeWidth={1.2} className="absolute -top-3 -right-3 text-brand-cream/10" />
              <div className="text-xs font-black uppercase tracking-[0.22em] text-brand-red mb-3">
                {t.calc.totalEyebrow}
              </div>
              <div className="flex items-end gap-2 mb-2">
                <div className="font-heading font-black text-6xl sm:text-7xl tracking-[-0.04em]" data-testid="calc-total">
                  ${total}
                </div>
                <div className="pb-3 text-base text-brand-cream/60 font-semibold">USD</div>
              </div>
              <div className="text-sm text-brand-cream/75 mb-7">
                {weight} {unitLabel} × ${rate.toFixed(2)}/{unitLabel} · {t.calc.allInclusive}
              </div>

              <div className="space-y-2 text-sm text-brand-cream/85">
                <div className="flex items-center justify-between border-t border-brand-cream/15 pt-2">
                  <span>{t.calc.transit}</span>
                  <span className="font-bold">5–7 {lang === "es" ? "días" : "days"}</span>
                </div>
                <div className="flex items-center justify-between border-t border-brand-cream/15 pt-2">
                  <span>{t.calc.delivery}</span>
                  <span className="font-bold">{t.calc.doorToDoor}</span>
                </div>
                <div className="flex items-center justify-between border-t border-brand-cream/15 pt-2">
                  <span>{t.calc.support}</span>
                  <span className="font-bold">24/7</span>
                </div>
              </div>

              <a
                href="https://trackmypackage.app/join/shipcostarica"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calc-cta"
                className="mt-7 inline-flex w-full items-center justify-center rounded-full bg-brand-red text-white font-heading font-bold text-base px-6 py-3.5 hover:bg-brand-cream hover:text-brand-navy transition-all"
              >
                {t.calc.cta}
              </a>
            </div>
          </div>

          <div
            className="relative mt-8 rounded-2xl bg-brand-red/10 border border-brand-red/20 px-5 py-4 text-sm text-brand-navy/80 leading-relaxed"
            data-testid="calc-last-mile-note"
          >
            {t.calc.lastMileNote}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CostCalculator;
